import { useMemo, useState, type CSSProperties } from 'react'
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom'

import { useAuth } from '@forbiz/web-kit'
import type { CatalogItem, OccasionType } from '@forbiz/contracts'

import { getFavorites, toggleFavorite } from '../lib/favorites.js'
import { ordersApi } from '../lib/orders.js'
import { defaultProductImage } from '../lib/productImage.js'
import { useAsync } from '../lib/useAsync.js'
import s from './CatalogPage.module.css'

/**
 * 전체 화환 카탈로그 (BIZ-82 데이터 위). 홈 '상품 둘러보기 › 전체 보기'에서 진입한다.
 *
 * 유형 탭(근조/축하/난기타) + 즐겨찾기(★, 기기별 로컬) 탭으로 둘러본다. 카드를 누르면
 * 상품 상세로 가고(진입점 a), 카드의 별로 즐겨찾기를 켜고 끈다. 데이터는 카탈로그 API 재사용.
 */

type Tab = 'FAV' | OccasionType

const OCC_TABS: { value: OccasionType; label: string }[] = [
  { value: 'FUNERAL', label: '근조' },
  { value: 'CELEBRATION', label: '축하' },
  { value: 'GIFT', label: '난·기타' },
]

export function CatalogPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const isCompany = user?.role === 'COMPANY_ADMIN' || user?.role === 'COMPANY_MEMBER'
  const companyId = user?.companyId ?? null

  // 홈 카테고리 카드/알림에서 ?occasion=..(또는 ?tab=FAV)로 넘어오면 해당 탭으로 연다(BIZ-110).
  const [params, setParams] = useSearchParams()
  const [tab, setTab] = useState<Tab>(() => {
    const occ = params.get('occasion')
    if (occ === 'FUNERAL' || occ === 'CELEBRATION' || occ === 'GIFT') return occ
    if (params.get('tab') === 'FAV') return 'FAV'
    return 'FUNERAL'
  })
  // 탭을 URL에도 남긴다 — 새로고침·뒤로가기에도 보던 탭이 유지되게(히스토리는 안 쌓음).
  const pickTab = (t: Tab) => {
    setTab(t)
    setParams(t === 'FAV' ? { tab: 'FAV' } : { occasion: t }, { replace: true })
  }
  const [favV, setFavV] = useState(0) // 즐겨찾기 토글 시 리렌더

  const { data, loading } = useAsync(
    () => (isCompany && companyId ? ordersApi.catalog(companyId) : Promise.resolve({ items: [] })),
    [isCompany, companyId],
  )

  if (user && !isCompany) return <Navigate to="/orders" replace />

  const items = data?.items ?? []
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const favIds = useMemo(() => new Set(getFavorites()), [favV])
  const shown = useMemo(
    () =>
      tab === 'FAV'
        ? items.filter((i) => favIds.has(i.productId))
        : items.filter((i) => i.occasionType === tab),
    [items, tab, favIds],
  )
  const occCount = (occ: OccasionType) => items.filter((i) => i.occasionType === occ).length
  const favCount = items.filter((i) => favIds.has(i.productId)).length

  const openDetail = (p: CatalogItem) => navigate(`/products/${p.productId}`)
  const star = (p: CatalogItem) => {
    toggleFavorite(p.productId)
    setFavV((v) => v + 1)
  }

  return (
    <div className={s.page}>
      <header className={s.head}>
        <h1 className={s.title}>상품 둘러보기</h1>
        <p className={s.lead}>보내는 마음에 꼭 맞는 화환을 찾아보세요.</p>
      </header>

      <div className={s.tabs} role="tablist" aria-label="화환 보기">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'FAV'}
          className={tab === 'FAV' ? `${s.tab} ${s.on}` : s.tab}
          onClick={() => pickTab('FAV')}
        >
          ★ 즐겨찾기<span className={s.tcount}>{favCount}</span>
        </button>
        {OCC_TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            role="tab"
            aria-selected={tab === t.value}
            className={tab === t.value ? `${s.tab} ${s.on}` : s.tab}
            onClick={() => pickTab(t.value)}
          >
            {t.label}
            <span className={s.tcount}>{occCount(t.value)}</span>
          </button>
        ))}
      </div>

      {loading ? (
        /* 스켈레톤 — 실제 카드와 같은 골격(이미지 타일 + 두 줄)이라 로딩 후 레이아웃이 안 튄다. */
        <div className={s.grid} aria-hidden="true">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className={s.skel}>
              <span className={s.skelIm} />
              <span className={s.skelLine} />
              <span className={`${s.skelLine} ${s.skelShort}`} />
            </div>
          ))}
        </div>
      ) : shown.length === 0 ? (
        <div className={s.empty}>
          <p className={s.muted}>
            {tab === 'FAV' ? '즐겨찾기한 상품이 없어요. 카드의 ★를 눌러 담아두세요.' : '이 유형의 상품이 아직 없어요.'}
          </p>
          {tab === 'FAV' ? (
            <button type="button" className={s.emptyCta} onClick={() => pickTab('FUNERAL')}>
              화환 둘러보러 가기 →
            </button>
          ) : null}
        </div>
      ) : (
        /* key=tab: 탭을 바꾸면 그리드를 새로 마운트해 등장 stagger가 다시 돈다. */
        <div className={s.grid} key={tab}>
          {shown.map((p, idx) => {
            const on = favIds.has(p.productId)
            return (
              <div
                key={p.productId}
                className={s.pcard}
                style={{ '--i': Math.min(idx, 7) } as CSSProperties}
                role="button"
                tabIndex={0}
                onClick={() => openDetail(p)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    openDetail(p)
                  }
                }}
              >
                <span
                  className={s.pim}
                  style={{ backgroundImage: `url(${p.imageUrl ?? defaultProductImage})` }}
                  aria-hidden="true"
                />
                <button
                  type="button"
                  className={on ? `${s.star} ${s.starOn}` : s.star}
                  aria-pressed={on}
                  aria-label={on ? '즐겨찾기 해제' : '즐겨찾기'}
                  title={on ? '즐겨찾기 해제' : '즐겨찾기'}
                  onClick={(e) => {
                    e.stopPropagation()
                    star(p)
                  }}
                >
                  {on ? '★' : '☆'}
                </button>
                <span className={s.pi}>
                  <span className={s.pn}>{p.name}</span>
                  {p.summary ? <span className={s.psum}>{p.summary}</span> : null}
                  <span className={s.pp}>{p.price.toLocaleString('ko-KR')}원</span>
                </span>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
